import * as Sentry from "@sentry/react";
import { AIError } from "../errors/sentryErrors";
import { getAuthToken } from "../contexts/AuthContext";
import { parseApiError } from "../utils/validation";
import { fetchWithSignIn, fetchRetry429 } from "../utils/sessionRetry";
import { BANNED_MESSAGE, announceUserBanned } from "../utils/roles";

// Fills in the code of a hand-built error and announces USER_BANNED (no retry, same message everywhere).
const markBanned = (err, body) => {
  err.code = err.code || body?.code || body?.error;
  if (err.code === "USER_BANNED") {
    err.message = BANNED_MESSAGE;
    announceUserBanned();
  }
  return err;
};

// Exported so every service module shares one definition. Duplicating these meant
// notificationApi.js silently omitted `credentials: "include"`, breaking cookie-only
// sessions on notification endpoints while the rest of the app worked.
export { API_URL } from "../config";
import { API_URL } from "../config";

export const getAuthHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
  };

  const token = getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

// Sends the funnel session cookie alongside the Bearer token, so a user who signed
// in via the funnel but hasn't been issued a token yet is still authenticated.
export const getAuthOptions = () => ({
  credentials: "include",
});

// Route lookups (optimize, validate-and-enhance) need no login, but a signed-in user gets the higher limit, so the login goes
// along. Over the limit (429) they are retried quietly.
export const routePost = (path, body) =>
  fetchRetry429(() =>
    fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: getAuthHeaders(),
      ...getAuthOptions(),
      body: JSON.stringify(body),
    }),
  );

export const validateTrip = async (tripData) => {
  const response = await fetch(`${API_URL}/api/itinerary/validate`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify(tripData),
  });

  return response.json();
};

export const getBudgetEstimate = async (tripData) => {
  const response = await fetchWithSignIn(() => fetch(`${API_URL}/api/itinerary/estimate-budget`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify(tripData),
  }));

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to estimate budget");
  }

  return response.json();
};

export const generateItinerary = async (tripData) => {
  const response = await fetchWithSignIn(() => fetch(`${API_URL}/api/itinerary/generate`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify(tripData),
  }));

  const data = await response.json();

  if (!response.ok) {
    if (response.status === 422 && data.validation) {
      const error = new Error("Validation failed");
      error.validation = data.validation;
      throw error;
    }
    throw new Error(data.error || "Failed to generate itinerary");
  }

  return data;
};

export const generateItineraryStreaming = async (tripData, { onEvent, signal } = {}) => {
  const response = await fetchWithSignIn(() => fetch(`${API_URL}/api/itinerary/generate/stream`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify(tripData),
    signal,
  }));

  if (!response.ok) {
    let errPayload = {};
    try { errPayload = await response.json(); } catch (_) { }
    if (response.status === 422 && errPayload.validation) {
      const e = new Error("Validation failed");
      e.validation = errPayload.validation;
      throw e;
    }
    throw new Error(errPayload.error || `Streaming request failed (${response.status})`);
  }

  if (!response.body) {
    throw new Error("Streaming not supported by the server response.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalItinerary = null;
  let streamError = null;

  const emit = (event, data) => {
    if (typeof onEvent === "function") {
      try { onEvent(event, data); } catch (e) {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "ai");
          scope.setTag("app", "web");
          scope.setTag("error_source", "server");
          Sentry.captureException(new AIError(e instanceof Error ? e.message : String(e)));
        });
        console.error("[stream] onEvent threw:", e);
      }
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const trimmed = frame.trim();
      if (!trimmed || trimmed.startsWith(":")) continue;

      let event = "message";
      let dataLines = [];
      for (const line of trimmed.split("\n")) {
        if (line.startsWith("event: ")) event = line.slice(7).trim();
        else if (line.startsWith("data: ")) dataLines.push(line.slice(6));
      }
      const dataStr = dataLines.join("\n");
      let data = null;
      try { data = JSON.parse(dataStr); } catch (e) {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "ai");
          scope.setTag("app", "web");
          scope.setTag("error_source", "server");
          Sentry.captureException(new AIError(e instanceof Error ? e.message : String(e)));
        });
        console.warn("[stream] bad JSON in frame:", dataStr.slice(0, 80)); continue;
      }

      if (event === "complete") finalItinerary = data;
      if (event === "error") streamError = data?.message || "Stream error";

      emit(event, data);

      if (event === "complete") {
        if (streamError) throw new Error(streamError);
        return finalItinerary;
      }
    }
  }

  if (streamError) throw new Error(streamError);
  if (finalItinerary) return finalItinerary;
  throw new Error("Stream ended without a complete itinerary.");
};

export const saveTrip = async (tripData, itinerary) => {
  const response = await fetch(`${API_URL}/api/itinerary/save`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify({ tripData, itinerary }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Please sign in to save your trip");
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to save trip");
  }

  return response.json();
};

export const updateTrip = async (tripId, tripData, itinerary) => {
  const response = await fetch(`${API_URL}/api/itinerary/trips/${tripId}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify({ tripData, itinerary }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Please sign in to update this trip");
    }
    if (response.status === 404) {
      throw new Error("Trip not found or you don't have permission to edit it");
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to update trip");
  }

  return response.json();
};

export const undoTripChange = async (tripId) => {
  const response = await fetch(`${API_URL}/api/itinerary/trips/${tripId}/undo`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });

  if (!response.ok) {
    if (response.status === 400) throw new Error("Nothing to undo");
    if (response.status === 401) throw new Error("Please sign in to undo changes");
    if (response.status === 404) throw new Error("Trip not found");
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to undo change");
  }

  return response.json();
};

export const updateTripItinerary = async (tripId, itineraryChanges) => {
  const response = await fetch(`${API_URL}/api/itinerary/trips/${tripId}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify({ itineraryChanges }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Please sign in to update this trip");
    }
    if (response.status === 404) {
      throw new Error("Trip not found or you don't have permission to edit it");
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to update trip");
  }

  return response.json();
};

export const getTrips = async ({ page = 1, limit = 12, search = "", bucket = "" } = {}) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  if (search.trim()) {
    params.append("search", search.trim());
  }

  // upcoming | ongoing | past — filtered server-side so each tab paginates over
  // its own trips rather than over one shared page.
  if (bucket) {
    params.append("bucket", bucket);
  }

  const response = await fetch(`${API_URL}/api/itinerary/trips?${params}`, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Please sign in to view your trips");
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to fetch trips");
  }

  return response.json();
};

export const getTrip = async (tripId) => {
  const response = await fetch(`${API_URL}/api/itinerary/trips/${tripId}`, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Please sign in to view this trip");
    }
    if (response.status === 404) {
      throw new Error("Trip not found or you don't have permission to view it");
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to fetch trip");
  }

  return response.json();
};

export const deleteTrip = async (tripId) => {
  const response = await fetch(`${API_URL}/api/itinerary/trips/${tripId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Please sign in to delete this trip");
    }
    if (response.status === 404) {
      throw new Error("Trip not found or you don't have permission to delete it");
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to delete trip");
  }

  return response.json();
};

export const convertCurrency = async (amount, fromCurrency, toCurrency) => {
  const response = await fetch(`${API_URL}/api/itinerary/convert-currency`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify({ amount, fromCurrency, toCurrency }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to convert currency");
  }

  return response.json();
};

export const getExchangeRates = async () => {
  const response = await fetch(`${API_URL}/api/itinerary/exchange-rates`, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to fetch exchange rates");
  }

  return response.json();
};

export const modifyItinerary = async (itinerary, message, formData, conversationHistory = []) => {
  const response = await fetchWithSignIn(() => fetch(`${API_URL}/api/itinerary/modify`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify({ itinerary, message, formData, conversationHistory }),
  }));

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Failed to modify itinerary");
  }

  return data;
};

export const searchHotels = async ({
  location,
  checkIn,
  checkOut,
  adults = 1,
  children = 0,
  infants = 0,
  starRating = 3,
  rooms = 1,
  limit = 5,
  accommodation = []
}) => {
  const response = await fetchWithSignIn(() => fetch(`${API_URL}/api/itinerary/hotels`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify({
      location,
      checkIn,
      checkOut,
      adults,
      children,
      infants,
      starRating,
      rooms,
      limit,
      accommodation
    }),
  }));

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Failed to search hotels");
  }

  return data;
};

export const getAirportCodes = async ({
  fromCity,
  toCity,
  fromLat = null,
  fromLon = null,
  toLat = null,
  toLon = null
}) => {
  try {
    const response = await fetch(`${API_URL}/api/itinerary/airport-codes`, {
      method: "POST",
      headers: getAuthHeaders(),
      ...getAuthOptions(),
      body: JSON.stringify({
        fromCity,
        toCity,
        fromLat,
        fromLon,
        toLat,
        toLon
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.warn("Airport codes fetch failed:", data.error);
      return { fromCode: null, toCode: null };
    }

    return data;
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag("feature", "ai");
      scope.setTag("app", "web");
      scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
      Sentry.captureException(new AIError(error instanceof Error ? error.message : String(error)));
    });
    console.warn("Airport codes fetch error:", error.message);
    return { fromCode: null, toCode: null };
  }
};

export const generatePackingChecklist = async (tripData, itinerary) => {
  try {
    const response = await fetchWithSignIn(() => fetch(`${API_URL}/api/itinerary/generate-packing-checklist`, {
      method: "POST",
      headers: getAuthHeaders(),
      ...getAuthOptions(),
      body: JSON.stringify({ tripData, itinerary }),
    }));

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to generate packing checklist");
    }

    return data.packingChecklist;
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag("feature", "ai");
      scope.setTag("app", "web");
      scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
      Sentry.captureException(new AIError(error instanceof Error ? error.message : String(error)));
    });
    console.warn("Packing checklist generation error:", error.message);
    return null;
  }
};

export const getCostEstimate = async (tripId) => {
  const response = await fetch(`${API_URL}/api/cost-estimation/${tripId}`, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to fetch cost estimate");
  }

  return response.json();
};

export const createCostEstimate = async (tripId) => {
  const response = await fetch(`${API_URL}/api/cost-estimation/${tripId}`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to create cost estimate");
  }

  return response.json();
};

export const refreshCostEstimate = async (tripId) => {
  const response = await fetch(`${API_URL}/api/cost-estimation/${tripId}/refresh`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Failed to refresh cost estimate");
  }

  return response.json();
};

export const uploadFileToS3WithProgress = (url, file, contentType, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", contentType);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        if (onProgress) onProgress(percentComplete);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error("S3 upload failed with status " + xhr.status));
      }
    };

    xhr.onerror = () => {
      reject(new Error("Network error during S3 upload"));
    };

    xhr.send(file);
  });
};


export const completeTrip = async (tripId) => {
  const response = await fetch(`${API_URL}/api/trips/${tripId}/complete`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    // TRIP_NOT_STARTED (409): the screen shows its own message for it.
    throw markBanned(new Error(error.message || error.error || "Failed to complete trip"), error);
  }
  return response.json();
};

// Takes the whole draft, not just the rating. The API destructures the body as
// `{ tripId, ...rest }` and whitelists `rest` server-side, so sending every field
// persists the complete review in one request — sending only the rating left the
// user's title and text unsaved until a later PATCH.
export const createReview = async (tripId, payload = {}) => {
  const response = await fetch(`${API_URL}/api/reviews`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify({ ...payload, tripId }),
  });
  if (!response.ok) {
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const updateReview = async (reviewId, data) => {
  const response = await fetch(`${API_URL}/api/reviews/${reviewId}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const submitReview = async (reviewId) => {
  const response = await fetch(`${API_URL}/api/reviews/${reviewId}/submit`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const deleteReview = async (reviewId) => {
  const response = await fetch(`${API_URL}/api/reviews/${reviewId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const getMediaGallery = async (tripId) => {
  const response = await fetch(`${API_URL}/api/trips/${tripId}/media`, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to fetch media");
  }
  return response.json();
};

export const presignMediaUpload = async (tripId, payload) => {
  const response = await fetch(`${API_URL}/api/trips/${tripId}/media/presign`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to presign upload");
  }
  return response.json();
};

export const finalizeMediaUpload = async (mediaId) => {
  const response = await fetch(`${API_URL}/api/media/${mediaId}/finalize`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to finalize upload");
  }
  return response.json();
};

// Public and unauthenticated. The client must not hard-code counts, byte ceilings or
// category lists — they are admin-tunable and change without a frontend deploy.
export const getMediaLimits = async () => {
  const response = await fetch(`${API_URL}/api/media/limits`, {
    method: "GET",
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to fetch media limits");
  }
  return response.json();
};

// Accepts any subset of { category, caption, sortOrder }. Promoting a photo to
// "cover" demotes the previous cover to "destination" in the same server-side
// transaction, so this is the whole cover-swap flow — no delete, no 409.
export const updateMedia = async (mediaId, patch) => {
  const response = await fetch(`${API_URL}/api/media/${mediaId}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify(patch),
  });
  if (!response.ok) {
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const deleteMedia = async (mediaId) => {
  const response = await fetch(`${API_URL}/api/media/${mediaId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to delete media");
  }
  return response.json();
};

export const getCreditsBalance = async () => {
  const response = await fetch(`${API_URL}/api/credits/balance`, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to fetch RoamPoints balance");
  }
  return response.json();
};

export const redeemCredits = async (productKey, amount) => {
  const response = await fetch(`${API_URL}/api/credits/redeem`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify({ productKey, amount }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const err = new Error(error.message || error.error || "Failed to redeem RoamPoints");
    err.status = response.status;
    err.code = error.error;
    err.allowed = error.allowed;
    throw markBanned(err, error);
  }
  return response.json();
};

export const publishTrip = async (tripId) => {
  const response = await fetch(`${API_URL}/api/trips/${tripId}/publish`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const err = new Error(error.message || error.error || "Failed to publish trip");
    err.status = response.status;
    err.issues = error.failed || [];
    throw err;
  }
  return response.json();
};

export const getReviewForTrip = async (tripId) => {
  const response = await fetch(`${API_URL}/api/reviews/trip/${tripId}`, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    if (response.status === 404) return null;
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to fetch review");
  }
  return response.json();
};

// Owner-only. `amount` values come live from the credit-rules table, which admins can
// change at any time — render the response, never a hard-coded figure.
export const getRewardsPreview = async (tripId) => {
  const response = await fetch(`${API_URL}/api/reviews/trip/${tripId}/rewards-preview`, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to fetch rewards preview");
  }
  return response.json();
};

// GET /api/community/posts/me is cursor-paginated (cursor/limit), not
// page-numbered — Dashboard.jsx used to call this with a page number that
// this function silently ignored entirely, so it never fetched anything but
// the first page.
export const getMyPublishedPosts = async (cursor = null, limit = 12) => {
  let url = `${API_URL}/api/community/posts/me?limit=${limit}`;
  if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to fetch published posts");
  }
  return response.json();
};

export const resubmitPost = async (postId, reviewNotes) => {
  const response = await fetch(`${API_URL}/api/community/posts/${postId}/resubmit`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify({ reviewNotes }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to resubmit post");
  }
  return response.json();
};

// The backend paginates this by cursor, not page number (see
// romaya-api's creditsService.getHistory) — `cursor` is the opaque
// `meta.nextCursor` string from a previous response, or null for the first page.
export const getCreditsHistory = async (cursor = null, limit = 50) => {
  const params = new URLSearchParams({ limit });
  if (cursor) params.set("cursor", cursor);
  const response = await fetch(`${API_URL}/api/credits/history?${params.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to fetch RoamPoints history");
  }
  return response.json();
};



export const getCreditsRules = async () => {
  const response = await fetch(`${API_URL}/api/credits/rules`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to fetch RoamPoints rules");
  }
  return response.json();
};

// Phase 4 - Community Endpoints

export const getCommunityStats = async () => {
  const response = await fetch(`${API_URL}/api/community/stats`, {
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) throw new Error("Failed to fetch community stats");
  return response.json();
};

export const getTrendingDestinations = async () => {
  const response = await fetch(`${API_URL}/api/community/destinations/trending`, {
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    cache: "no-store",
  });
  if (!response.ok) throw new Error("Failed to fetch trending destinations");
  return response.json();
};

export const getFeaturedPosts = async () => {
  const response = await fetch(`${API_URL}/api/community/featured`, {
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    cache: "no-store",
  });
  if (!response.ok) throw new Error("Failed to fetch featured posts");
  return response.json();
};

export const getCommunityFeed = async (section = "recent", cursor = null, limit = 12) => {
  let url = `${API_URL}/api/community/feed?section=${section}&limit=${limit}`;
  if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
  const response = await fetch(url, {
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    cache: "no-store",
  });
  if (!response.ok) {
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const getCommunityPostDetail = async (slug, preview = false) => {
  let url = `${API_URL}/api/community/posts/${slug}`;
  if (preview) url += "?preview=true";
  
  const headers = getAuthHeaders();
  const response = await fetch(url, {
    headers: headers,
    ...getAuthOptions()
  });
  if (!response.ok) {
    if (response.status === 404) return null;
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const getCommunityFilterFacets = async () => {
  const response = await fetch(`${API_URL}/api/community/filters`);
  if (!response.ok) throw new Error("Failed to fetch filter facets");
  return response.json();
};

export const searchCommunityPosts = async (params) => {
  const query = new URLSearchParams();
  for (const key in params) {
    if (params[key]) {
      query.append(key, params[key]);
    }
  }
  const response = await fetch(`${API_URL}/api/community/search?${query.toString()}`, {
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    cache: "no-store",
  });
  if (!response.ok) {
    // Surface the server's reason (e.g. INVALID_TRIP_TYPE / INVALID_MONTH)
    // instead of a generic string — filter mismatches are otherwise silent.
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

// Phase 5 - Engagement Endpoints

export const likePost = async (postId) => {
  const response = await fetch(`${API_URL}/api/community/posts/${postId}/like`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const err = new Error(error.message || error.error || "Failed to like post");
    err.status = response.status;
    err.retryAfter = error.retryAfter;
    throw markBanned(err, error);
  }
  return response.json();
};

export const unlikePost = async (postId) => {
  // DELETE on /like — there is no /unlike route. This posted to /posts/:id/unlike
  // and got a 404 every time, so a like could be added but never removed.
  // `unsavePost` below already used the DELETE-on-the-same-path convention.
  const response = await fetch(`${API_URL}/api/community/posts/${postId}/like`, {
    method: "DELETE",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const savePost = async (postId) => {
  const response = await fetch(`${API_URL}/api/community/posts/${postId}/save`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const unsavePost = async (postId) => {
  const response = await fetch(`${API_URL}/api/community/posts/${postId}/save`, {
    method: "DELETE",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to unsave post");
  }
  return response.json();
};

export const getMySavedPosts = async (cursor = null, limit = 12) => {
  // Mounted under /api/users, not /api/community (see routes/users.js).
  let url = `${API_URL}/api/users/me/saved?limit=${limit}`;
  if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
  
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) throw new Error("Failed to fetch saved posts");
  return response.json();
};

export const getMyLikedPosts = async (cursor = null, limit = 12) => {
  // Mounted under /api/users, not /api/community (see routes/users.js).
  let url = `${API_URL}/api/users/me/liked?limit=${limit}`;
  if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
  
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) throw new Error("Failed to fetch liked posts");
  return response.json();
};

export const getPostComments = async (postId, cursor = null, limit = 10) => {
  let url = `${API_URL}/api/community/posts/${postId}/comments?limit=${limit}`;
  if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
  
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) throw new Error("Failed to fetch comments");
  return response.json();
};

export const addPostComment = async (postId, body) => {
  const response = await fetch(`${API_URL}/api/community/posts/${postId}/comments`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify({ body }),
  });
  if (!response.ok) {
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const deletePostComment = async (commentId) => {
  const response = await fetch(`${API_URL}/api/community/comments/${commentId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || "Failed to delete comment");
  }
  return response.json();
};

export const reportPost = async (postId, reason, note) => {
  const response = await fetch(`${API_URL}/api/community/posts/${postId}/report`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify({ reason, note }),
  });
  if (!response.ok) {
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const reportComment = async (commentId, reason, note) => {
  const response = await fetch(`${API_URL}/api/community/comments/${commentId}/report`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify({ reason, note }),
  });
  if (!response.ok) {
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const trackPostShare = async (postId, channel) => {
  const token = getAuthToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  
  // Fire and forget
  fetch(`${API_URL}/api/community/posts/${postId}/share`, {
    method: "POST",
    headers: headers,
    ...getAuthOptions(),
    body: JSON.stringify({ channel }),
  }).catch(() => {});
};

export const copyPost = async (postId) => {
  const response = await fetch(`${API_URL}/api/community/posts/${postId}/copy`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const err = new Error(error.message || error.error || "Failed to copy post");
    err.status = response.status;
    err.retryAfter = error.retryAfter;
    throw markBanned(err, error);
  }
  return response.json();
};

// --- Profile & Follow (Phase 8) ---

export const getPublicProfile = async (handle) => {
  const token = getAuthToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(`${API_URL}/api/users/${handle}`, {
    method: "GET",
    headers,
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const err = new Error(error.message || error.error || "Failed to fetch profile");
    err.status = response.status;
    throw err;
  }
  return response.json();
};

export const getMyProfile = async () => {
  const response = await fetch(`${API_URL}/api/users/me/profile`, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const err = new Error(error.message || error.error || "Failed to fetch profile");
    err.status = response.status;
    throw err;
  }
  return response.json();
};

export const updateMyProfile = async (data) => {
  const response = await fetch(`${API_URL}/api/users/me/profile`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const err = new Error(error.message || error.error || "Failed to update profile");
    err.status = response.status;
    err.retryAfter = error.retryAfter;
    err.daysLeft = error.daysLeft; // Handle cooldown
    throw err;
  }
  return response.json();
};

export const followUser = async (handle) => {
  const response = await fetch(`${API_URL}/api/users/${handle}/follow`, {
    method: "POST",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const err = await parseApiError(response);
    throw err;
  }
  return response.json();
};

export const unfollowUser = async (handle) => {
  const response = await fetch(`${API_URL}/api/users/${handle}/follow`, {
    method: "DELETE",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const err = new Error(error.message || error.error || "Failed to unfollow user");
    err.status = response.status;
    err.retryAfter = error.retryAfter;
    throw err;
  }
  return response.json();
};

export const getCreditRules = async () => {
  const response = await fetch(`${API_URL}/api/admin/credit-rules`, {
    method: "GET",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const err = new Error(error.message || error.error || "Failed to fetch credit rules");
    err.status = response.status;
    throw err;
  }
  return response.json();
};

export const updateCreditRule = async (code, patch) => {
  const response = await fetch(`${API_URL}/api/admin/credit-rules/${code}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    ...getAuthOptions(),
    body: JSON.stringify(patch),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const err = new Error(error.message || error.error || "Failed to update credit rule");
    err.status = response.status;
    err.code = error.error;
    throw err;
  }
  return response.json();
};
